import { useMutation } from '@apollo/client'
import { Box, Dialog, DialogActions, DialogContent, Grid, Typography } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { OutlinedCross } from '../../../assets/icons'
import { FORWARD_TO_RIGHTMART } from '../../../graphql/mutations'
import { useNotification } from '../../../utils/hooks'
import { Button, Input } from '../../atoms'

interface IDialogContent {
  title: string
  subtitle: string
  message: string
  variables: any
  callback(variables?: any): void
}

interface Attributes {
  policyNumber: string
  claimDate: string
  legalArea: string
  mobileNumber?: string
  name: string
  phoneNumber?: string
  comments?: string
  commentsLegalAdvice?: string
  contactPersonName?: string
  contactPersonPhoneNumber?: string
  contactPersonMobileNumber?: string
  relationToPolicyholder?: string
}

interface ForwardRightmartButtonProps {
  options?: any
  label: string
  value?: any[]
  disabled?: boolean
  dialogContent?: IDialogContent
  helperText?: string
}

const ForwardRightmartButton: React.FC<ForwardRightmartButtonProps> = ({
  label,
  value = [],
  disabled = false,
  options = undefined,
  dialogContent = undefined,
  helperText = undefined
}) => {
  const { t } = useTranslation(['common', 'forwardRightmart'])
  const { setNotification } = useNotification()
  const [attributes, setAttributes] = useState<Attributes>(dialogContent?.variables)
  useEffect(() => {
    setAttributes(dialogContent?.variables)
  }, [dialogContent?.variables, dialogContent?.variables?.legalArea])
  const [open, setOpen] = useState(false)
  const isFormValid = useCallback(() => {
    let valid = true
    if (!(attributes.name && attributes.name.length > 0)) {
      valid = false
    }
    if (!(attributes.commentsLegalAdvice && attributes.commentsLegalAdvice.length > 0)) {
      valid = false
    }
    if (!(attributes.policyNumber && attributes.policyNumber.length > 0)) {
      valid = false
    }
    if (!(attributes.claimDate && attributes.claimDate.length > 0)) {
      valid = false
    }
    if (!(attributes.legalArea && attributes.legalArea.length > 0)) {
      valid = false
    }

    return valid
  }, [
    attributes?.name,
    attributes?.commentsLegalAdvice,
    attributes?.policyNumber,
    attributes?.claimDate,
    attributes?.legalArea
  ])

  const [formValid, setFormValid] = useState(isFormValid())
  useEffect(() => {
    setFormValid(isFormValid())
  }, [isFormValid])

  const [submitMutation] = useMutation(FORWARD_TO_RIGHTMART, {
    onCompleted: () => {
      setOpen(false)
      setNotification({
        open: true,
        variant: 'success',
        title: t('success'),
        message: t('forwardRightmart:success')
      })
      dialogContent?.callback?.(attributes)
    },
    onError: error => {
      setOpen(false)
      setNotification({ open: true, variant: 'error', title: t('error'), message: error?.message })
    }
  })
  return (
    <>
      <Button color="primary" onClick={() => setOpen(true)} fullWidth disabled={disabled}>
        {label}
      </Button>

      {helperText && (
        <Typography
          variant="caption"
          sx={{ paddingTop: 1, display: 'flex', margin: '0px 14px', width: '100%' }}
        >
          {helperText}
        </Typography>
      )}
      <Typography
        variant="caption"
        sx={{ display: 'flex', justifyContent: 'center', width: '100%', paddingTop: '5px' }}
      >
        {value?.[0]}
      </Typography>
      <Dialog open={open} maxWidth="lg" data-testid="sms-modal">
        <DialogContent>
          <>
            <Typography variant="h5">{dialogContent?.title}</Typography>
            <Typography variant="caption" sx={{ pt: 1 }}>
              {dialogContent?.subtitle}
            </Typography>
            <Grid container sx={{ mt: 1 }} spacing={2}>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.policyNumber}
                  label={t('caseProperty:policyNumber')}
                  fullWidth
                  required
                  onChange={({ target: { value: policyNumber } }) =>
                    setAttributes((vars: any) => ({ ...vars, policyNumber }))
                  }
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.claimDate}
                  label={t('caseProperty:claimDate')}
                  fullWidth
                  required
                  onChange={({ target: { value: claimDate } }) =>
                    setAttributes((vars: any) => ({ ...vars, claimDate }))
                  }
                />
              </Grid>
              <Grid size={{ md: 12 }}>
                <Typography variant="h6">{t('caseProperty:policyholder')}</Typography>
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.name}
                  label={t('forwardRightmart:name')}
                  required
                  fullWidth
                  onChange={({ target: { value: name } }) =>
                    setAttributes((vars: any) => ({ ...vars, name }))
                  }
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.phoneNumber}
                  label={t('caseProperty:phoneNumber')}
                  fullWidth
                  onChange={({ target: { value: phoneNumber } }) =>
                    setAttributes((vars: any) => ({ ...vars, phoneNumber }))
                  }
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.mobileNumber}
                  label={t('caseProperty:mobileNumber')}
                  fullWidth
                  onChange={({ target: { value: mobileNumber } }) =>
                    setAttributes((vars: any) => ({ ...vars, mobileNumber }))
                  }
                />
              </Grid>
              <Grid size={{ md: 12 }}>
                <Typography variant="h6">{t('caseProperty:contactPerson')}</Typography>
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.contactPersonName}
                  label={t('forwardRightmart:contactPersonName')}
                  fullWidth
                  onChange={({ target: { value: contactPersonName } }) =>
                    setAttributes((vars: any) => ({ ...vars, contactPersonName }))
                  }
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.relationToPolicyholder}
                  label={t('forwardRightmart:relationToPolicyholder')}
                  fullWidth
                  onChange={({ target: { value: relationToPolicyholder } }) =>
                    setAttributes((vars: any) => ({ ...vars, relationToPolicyholder }))
                  }
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.contactPersonPhoneNumber}
                  label={t('caseProperty:phoneNumber')}
                  fullWidth
                  onChange={({ target: { value: contactPersonPhoneNumber } }) =>
                    setAttributes((vars: any) => ({ ...vars, contactPersonPhoneNumber }))
                  }
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.contactPersonMobileNumber}
                  label={t('caseProperty:mobileNumber')}
                  fullWidth
                  onChange={({ target: { value: contactPersonMobileNumber } }) =>
                    setAttributes((vars: any) => ({ ...vars, contactPersonMobileNumber }))
                  }
                />
              </Grid>
              <Grid size={{ md: 12 }}>
                <Typography variant="h6">{t('caseProperty:caseInformations')}</Typography>
              </Grid>
              <Grid size={{ md: 12 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.legalArea}
                  label={t('forwardRightmart:legalArea')}
                  disabled
                  required
                  fullWidth
                  onChange={({ target: { value: legalArea } }) =>
                    setAttributes((vars: any) => ({ ...vars, legalArea }))
                  }
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.comments}
                  label={t('caseProperty:caseComments')}
                  multiline
                  minRows={5}
                  fullWidth
                  onChange={({ target: { value: comments } }) =>
                    setAttributes((vars: any) => ({ ...vars, comments }))
                  }
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <Input
                  sx={{ whiteSpace: 'pre-line' }}
                  value={attributes?.commentsLegalAdvice}
                  label={t('caseProperty:caseCommentsLegalAdvice')}
                  required
                  multiline
                  minRows={5}
                  fullWidth
                  onChange={({ target: { value: commentsLegalAdvice } }) =>
                    setAttributes((vars: any) => ({ ...vars, commentsLegalAdvice }))
                  }
                />
              </Grid>
            </Grid>
          </>
        </DialogContent>
        <DialogActions>
          <Box sx={{ marginRight: '39px', marginBottom: '42px' }}>
            <Button
              sx={{ color: 'secondary', marginRight: '15px' }}
              onClick={() => setOpen(false)}
              startIcon={<OutlinedCross />}
            >
              {t('common:abort')}
            </Button>
            <Button
              sx={{ color: 'primary' }}
              disabled={!formValid}
              onClick={() => {
                if (formValid) {
                  submitMutation({
                    variables: {
                      ...options?.variables,
                      attributes
                    }
                  })
                }
              }}
            >
              {t('sms:sendNow')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ForwardRightmartButton
